// ─── SectionGroup ────────────────────────────────────────────────────────────
//
// A collapsible section wrapper used by Phase 7 to group related cards
// (e.g. "Biological" for Labs + Metabolic, "Device Data" for Apple Watch)
// under a single visible header so the secondary content below the three
// hero sections reads as clearly secondary and does not compete for
// attention with the Primary Insight, System Overview, and Pattern
// Interpretation above.
//
// Behavior:
//   - Collapsed by default (user decision: secondary content should not
//     inflate the first scroll)
//   - Header click or Enter/Space toggles open/closed
//   - Semantic: <section aria-labelledby> with a <button aria-expanded>
//     header and the children wrapped in a panel with id referenced by
//     aria-controls
//   - `testId` lands on the root section so tests + devtools can
//     positively identify it
//   - `defaultOpen` lets individual callers override the "collapsed by
//     default" rule when a particular group is genuinely primary
//
// Presentation-only. No business logic.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState, useId, useEffect, useRef } from 'react';
import { IconChevronDown } from '../Icons';

interface Props {
  /** Section heading shown in the header. */
  title: string;
  /** Optional one-line summary shown under the title when collapsed. */
  subtitle?: string;
  /** Cards to render inside the collapsible body. */
  children: React.ReactNode;
  /** Start open instead of collapsed. Default false. */
  defaultOpen?: boolean;
  /** Testid + DOM marker for positive identification. */
  testId?: string;
  /**
   * When true the toggle is disabled and the header shows a "no data yet"
   * pill instead of the chevron. Lets callers signal that a group has no
   * meaningful content to expand without hiding the section entirely
   * (which would make it unclear that the group exists at all).
   */
  isEmpty?: boolean;
  /** Accessible label for the empty-state pill. Defaults to "No data yet". */
  emptyLabel?: string;
}

export default function SectionGroup({
  title,
  subtitle,
  children,
  defaultOpen = false,
  testId,
  isEmpty = false,
  emptyLabel = 'No data yet',
}: Props) {
  const [open, setOpen] = useState(defaultOpen && !isEmpty);
  const autoId = useId();
  const headingId = `${autoId}-heading`;
  const panelId = `${autoId}-panel`;

  // If the section mounted in the empty state, the open initializer above
  // resolved to `false` even when defaultOpen was true. When data later
  // arrives (isEmpty transitions true → false), respect the original
  // defaultOpen intent and auto-open exactly once. After firing once the
  // ref is cleared so the user's subsequent toggles are never overridden.
  const pendingAutoOpen = useRef(defaultOpen && isEmpty);
  useEffect(() => {
    if (pendingAutoOpen.current && !isEmpty) {
      setOpen(true);
      pendingAutoOpen.current = false;
    }
  }, [isEmpty]);

  return (
    <section
      aria-labelledby={headingId}
      className="card !p-0 overflow-hidden"
      data-testid={testId}
      data-phase="7"
      data-open={open}
      data-empty={isEmpty}
    >
      <button
        type="button"
        onClick={() => !isEmpty && setOpen((v) => !v)}
        aria-expanded={isEmpty ? false : open}
        aria-controls={panelId}
        aria-disabled={isEmpty}
        disabled={isEmpty}
        className="w-full flex items-center justify-between gap-3 px-7 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40 hover:bg-arete-sageLight/40 dark:hover:bg-white/5 motion-reduce:transition-none transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        <div className="min-w-0 flex-1">
          <h2
            id={headingId}
            className="section-label text-arete-forest dark:text-gray-200"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="mt-0.5 text-xs text-arete-muted leading-snug">
              {subtitle}
            </p>
          )}
        </div>
        {isEmpty ? (
          <span
            className="flex-shrink-0 text-[11px] font-medium text-arete-muted bg-gray-100 dark:bg-white/5 px-2.5 py-1 rounded-full"
            aria-label={emptyLabel}
          >
            {emptyLabel}
          </span>
        ) : (
          <IconChevronDown
            size={18}
            className={`text-arete-muted flex-shrink-0 motion-reduce:transition-none transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {open && !isEmpty && (
        <div
          id={panelId}
          className="px-7 pb-6 pt-0 flex flex-col gap-5"
          role="region"
          aria-labelledby={headingId}
        >
          {children}
        </div>
      )}
    </section>
  );
}
